import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, createRef, useCallback, useRef } from 'react';
import _, { cloneDeep as _cloneDeep, findIndex as _findIndex, find as _find, forEach as _forEach } from 'lodash';
import { Tooltip } from 'react-tooltip';
import { Button, ButtonGroup } from 'react-bootstrap';
import { confirmAlert } from 'js/wrapper/CustomConfirmAlert';
import { legacyInjectIntl } from 'js/wrapper/injectIntl';
import withRouter from 'js/withRouter';
import { connect } from 'react-redux';
import FormInput from 'components/forms/FormInput';
import Loader from 'components/layout/Loader';
import ReactDOM from 'react-dom';
import Axios from 'axios';
import { setLoginForwardUrl } from 'actions/LoginActions';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DraggableWrapper } from 'cc-editor/components/DraggableWrapper';
import EditorBlockFTs from 'cc-editor/components/EditorBlockFTs';
import PreviewBlockFTs from 'cc-editor/components/PreviewBlockFTs';
import { removeBlock, resetBlock } from '../actions/Blocks';
import { getCampaignsR, getEditionR, initTemplateR, save_ContentR, saveToR, setChangesR, setVariables, } from '../../../actions/EditorActions';
// ============================
// Helper: extract numeric attr
// ============================
const getNumericValue = (str, attributeName) => {
    const re = new RegExp(`${attributeName}="\\s*([^"\\s]*)`, 'i');
    const match = re.exec(str);
    return match ? Number(match[1]) : 0;
};
// ============================
// ContextModal
// ============================
function ContextModal({ children }) {
    const contextModalElement = document.getElementById('context-modal');
    if (!contextModalElement)
        return null;
    return ReactDOM.createPortal(children, contextModalElement);
}
// ============================
// uploadPDF helper
// ============================
const uploadPDF = (formData) => {
    return Axios.post('/api/editions/uploads/pdfs.json', formData);
};
// ============================
// Functional Component
// ============================
const DraggableContainerFTs = (props) => {
    var _a, _b;
    const { intl, appIntl, blocks } = props;
    // use nested messages from Redux-Intl as requested
    const appMessages = (_b = (_a = appIntl === null || appIntl === void 0 ? void 0 : appIntl.messages) === null || _a === void 0 ? void 0 : _a.messages) !== null && _b !== void 0 ? _b : {};
    const { updateInnerBlock } = props;
    // ----------------------
    // local state
    // ----------------------
    const [bgColor, setBgColor] = useState({});
    const [blockForUrl, setBlockForUrl] = useState(undefined);
    const [showUrl, setShowUrl] = useState(false);
    const [imgUrl, setImgUrl] = useState('');
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    // small rerender trigger used where class used forceUpdate()
    const [, setTick] = useState(0);
    const forceUpdate = () => setTick((t) => t + 1);
    // ----------------------
    // Draggable-specific state
    // deltaPosition: array of { blockId, x, y }
    // ----------------------
    const [deltaPosition, setDeltaPosition] = useState([]);
    const dragOffsets = useRef({});
    const sensors = useSensors(useSensor(PointerSensor, {
        activationConstraint: {
            distance: 3, // Starts only after the mouse has moved 3 pixels.
        },
    }));
    const handleDragEnd = useCallback((event) => {
        var _a, _b;
        const { active } = event;
        const idInnerBlock = active.id;
        const { id } = props;
        const data = _cloneDeep(blocks);
        const block = _find(data, {
            id: idInnerBlock,
        });
        if (!block) {
            return;
        }
        const offset = dragOffsets.current[idInnerBlock];
        if (!offset) {
            return;
        }
        block.position_x = ((_a = block.position_x) !== null && _a !== void 0 ? _a : 0) + offset.x;
        block.position_y = ((_b = block.position_y) !== null && _b !== void 0 ? _b : 0) + offset.y;
        updateInnerBlock(id, data, null, null, null, true);
    }, [blocks, updateInnerBlock]);
    // ----------------------
    // Background update
    // ----------------------
    const updateBackground = (bgColorID, blockId) => {
        setBgColor((prev) => (Object.assign(Object.assign({}, prev), { [blockId]: bgColorID })));
    };
    // ----------------------
    // Image upload wrapper (calls prop)
    // ----------------------
    const _imageUpload = (id, image, imageKey, name, data, width, height, filename, thumb, copyright, updateF = null, sliderImage = null, ownKey = '', imgDescription = '', autoplay = false, duration = 0, imageOrVideoEditWidth = 0, imageOrVideoEditHeight = 0, errors, filterAsString) => {
        var _a;
        (_a = props.imageUpload) === null || _a === void 0 ? void 0 : _a.call(props, id, image, imageKey, name, data, width, height, filename, thumb, copyright, updateParams, sliderImage, ownKey, imgDescription, autoplay, duration, imageOrVideoEditWidth, imageOrVideoEditHeight, errors, filterAsString);
    };
    // ----------------------
    // Param update helpers (copied & converted)
    // ----------------------
    const updateSpecialBlockAttributesInContainer = (id, name, value, thumb, errors, filter, url) => {
        const error = {};
        const params = [{ name, value, thumb, error, filter, url }];
        updateParams(id, params, errors);
        return true;
    };
    const updateParam = (id, name, value, errors, name2 = '', value2 = '', page, initialUpdate, type) => {
        const error = {};
        if (errors !== null && errors !== undefined) {
            _forEach(errors, ({ overflow, default_image, resolution_warning }) => {
                error.overflow = overflow;
                error.default_image = default_image;
                error.resolution_warning = resolution_warning;
            });
        }
        let params = [{ name, value, error }];
        if (name2 !== '' && value2 !== '') {
            params = [
                { name, value, error },
                { name: name2, value: value2, error: errors },
            ];
        }
        updateParams(id, params, errors, page, initialUpdate, type);
        return true;
    };
    const updateParams = (blockId, paramList, errors, page = null, initialUpdate, type) => {
        const { spaceConsumed, usedMedia, id } = props;
        if (!blocks)
            return;
        const data = _cloneDeep(blocks);
        const element = _find(data, { id: blockId });
        if (!element)
            return;
        const { params } = element;
        _forEach(paramList, ({ name, value, error, filter, url }) => {
            const paramIndex = _findIndex(params, ['name', name]);
            const paramValue = { name, value, errors: error, filter, url };
            if (paramIndex >= 0) {
                params[paramIndex] = paramValue;
            }
            else if (type === 'creatomate') {
                addAndSortParams(params, paramValue);
            }
            else {
                params.push(paramValue);
            }
        });
        if (initialUpdate) {
            updateInnerBlock(id, data, spaceConsumed, usedMedia, page, null, blockId, initialUpdate, paramList, type);
        }
        else {
            updateInnerBlock(id, data, spaceConsumed, usedMedia, page, null, blockId);
        }
    };
    const addAndSortParams = (params, paramValue) => {
        params.push(paramValue);
        params.sort((a, b) => {
            const numA = parseInt(a.name.match(/\d+$/)[0], 10);
            const numB = parseInt(b.name.match(/\d+$/)[0], 10);
            return numA - numB;
        });
    };
    // ----------------------
    // remove / reset block helpers
    // ----------------------
    const removeBlocksDefault = (sourceRasterId, sourceSpace) => {
        var _a;
        const { spaceConsumed, usedMedia, id } = props;
        if (!blocks)
            return;
        const changes = removeBlock(0, [{ blocks, space_consumed: spaceConsumed }], null, sourceRasterId, sourceSpace, true, usedMedia);
        updateInnerBlock(id, changes.contentPages[0].blocks, changes.contentPages[0].space_consumed, changes.usedMedia);
        removeBlocksChildAuto(sourceRasterId);
        (_a = props.deleteSelectedColor) === null || _a === void 0 ? void 0 : _a.call(props, sourceRasterId, id);
    };
    const removeBlocksChildAuto = (sourceRasterId) => {
        var _a;
        if (!blocks)
            return;
        const indexOfParent = _.findIndex(blocks, ['id', sourceRasterId]);
        const parent = (_a = blocks[indexOfParent]) === null || _a === void 0 ? void 0 : _a.block_combination_parent;
        if (parent !== undefined && parent !== '') {
            _forEach(blocks, (block) => {
                const blockCombinationChild = block.block_combination_child.split(',');
                _forEach(blockCombinationChild, (blockCC) => {
                    if (blockCC === parent) {
                        setDelay(block.id, block.page_space_needed);
                    }
                });
            });
        }
    };
    const setDelay = (blockId, blockPageSpaceNeeded) => {
        window.setTimeout(() => {
            removeBlocksChild(blockId, blockPageSpaceNeeded);
        }, 100);
    };
    const removeBlocksChild = (sourceRasterId, sourceSpace) => {
        const { spaceConsumed, usedMedia, id, deleteSelectedColor } = props;
        if (!blocks)
            return;
        deleteSelectedColor === null || deleteSelectedColor === void 0 ? void 0 : deleteSelectedColor(sourceRasterId, id);
        const changes = removeBlock(0, [{ blocks, space_consumed: spaceConsumed }], null, sourceRasterId, sourceSpace, true, usedMedia);
        updateInnerBlock(id, changes.contentPages[0].blocks, changes.contentPages[0].space_consumed, changes.usedMedia);
    };
    // ----------------------
    // delete / copy / paste / move / reset handlers (use appMessages)
    // ----------------------
    const deleteBlock = (e) => {
        var _a;
        const sourceRasterId = e.currentTarget.getAttribute('data-block');
        const sourceSpace = parseInt(e.currentTarget.getAttribute('data-space'), 10);
        if (!sourceRasterId)
            return;
        const indexOfParent = _.findIndex(blocks, ['id', sourceRasterId]);
        const parent = (_a = blocks === null || blocks === void 0 ? void 0 : blocks[indexOfParent]) === null || _a === void 0 ? void 0 : _a.block_combination_parent;
        let warning = appMessages.editor.delete_selected_content_block;
        if (parent !== undefined && parent !== '') {
            warning = appMessages.editor.delete_selected_content_block + ' ' + appMessages.editor.delete_children_of_parents;
        }
        const options = {
            title: appMessages.editor.delete_content + '?',
            message: warning,
            buttons: [
                {
                    label: appMessages.editor.yes_delete,
                    onClick: () => {
                        removeBlocksDefault(sourceRasterId, sourceSpace);
                    },
                },
                {
                    label: appMessages.editor.rather_not,
                    onClick: () => { },
                },
            ],
            childrenElement: () => _jsx("div", {}),
            closeOnEscape: true,
            closeOnClickOutside: true,
        };
        confirmAlert(options);
    };
    const copyBlock = (e, id) => {
        var _a;
        (_a = props.copyBlock) === null || _a === void 0 ? void 0 : _a.call(props, e, 'inner');
        forceUpdate();
    };
    const pasteBlock = (e) => {
        var _a;
        (_a = props.pasteBlock) === null || _a === void 0 ? void 0 : _a.call(props, e, 'inner');
        forceUpdate();
    };
    const moveBlock = (e) => {
        const { spaceConsumed, usedMedia, id } = props;
        const sourceIndex = parseInt(e.currentTarget.getAttribute('data-index'), 10);
        const targetIndex = parseInt(e.currentTarget.getAttribute('data-target'), 10);
        if (!blocks)
            return;
        const changedBlocks = _cloneDeep(blocks);
        changedBlocks[targetIndex] = _cloneDeep(blocks[sourceIndex]);
        changedBlocks[sourceIndex] = _cloneDeep(blocks[targetIndex]);
        updateInnerBlock(id, changedBlocks, spaceConsumed, usedMedia);
    };
    const resBlock = (e) => {
        const { spaceConsumed, usedMedia, templateBlocks, id } = props;
        const options = {
            title: appMessages.editor.reset_content + '?',
            message: appMessages.editor.reset_content_block_to_root,
            buttons: [
                {
                    label: appMessages.editor.yes_reset,
                    onClick: () => {
                        if (!blocks)
                            return;
                        const rasterId = e.currentTarget.getAttribute('data-block');
                        const templateId = e.currentTarget.getAttribute('data-template');
                        if (!rasterId || !templateId)
                            return;
                        const changes = resetBlock(rasterId, templateId, [{ blocks }], 0, templateBlocks, usedMedia);
                        updateInnerBlock(id, changes.contentPages[0].blocks, spaceConsumed, changes.usedMedia);
                        forceUpdate();
                    },
                },
                {
                    label: appMessages.editor.rather_not,
                    onClick: () => { },
                },
            ],
            childrenElement: () => _jsx("div", {}),
            closeOnEscape: true,
            closeOnClickOutside: true,
        };
        confirmAlert(options);
    };
    // ----------------------
    // initialize delta positions when blocks change
    // ----------------------
    useEffect(() => {
        if (!blocks)
            return;
        const _delta = _cloneDeep(deltaPosition);
        _forEach(blocks, (block) => {
            const idx = _.findIndex(_delta, (d) => d.blockId === block.id);
            if (idx === -1) {
                const blockTemplate = block.template ? block.template.toLowerCase() : '';
                let posX = getNumericValue(blockTemplate, 'defaultpositionx') || 0;
                let posY = getNumericValue(blockTemplate, 'defaultpositiony') || 0;
                if (block.position_x !== undefined && block.position_x !== null)
                    posX = block.position_x;
                if (block.position_y !== undefined && block.position_y !== null)
                    posY = block.position_y;
                _delta.push({ blockId: block.id, x: posX, y: posY });
            }
        });
        setDeltaPosition(_delta);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blocks]);
    // ----------------------
    // Image URL modal / helpers (ContextModal)
    // ----------------------
    const handleIEClose = () => {
        setShowUrl(false);
    };
    const getImageUrlSettings = (block) => {
        var _a, _b, _c, _d;
        setBlockForUrl(block);
        if (((_b = (_a = block.params) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.url) !== undefined && ((_d = (_c = block.params) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.url) !== null) {
            setImgUrl(block.params[0].url);
            setShowUrl(true);
        }
        else {
            setImgUrl('');
            setShowUrl(true);
        }
    };
    const saveUrl = () => {
        var _a, _b, _c, _d, _e;
        const localBlock = blockForUrl;
        if (!localBlock)
            return;
        const { id } = localBlock;
        let value = '';
        let name = '';
        let errors = null;
        let thumb = '';
        let filter = '';
        if (localBlock.params.length > 0) {
            value = (_a = localBlock.params[0]) === null || _a === void 0 ? void 0 : _a.value;
            name = (_b = localBlock.params[0]) === null || _b === void 0 ? void 0 : _b.name;
            errors = (_c = localBlock.params[0]) === null || _c === void 0 ? void 0 : _c.errors;
            thumb = (_d = localBlock.params[0]) === null || _d === void 0 ? void 0 : _d.thumb;
            filter = (_e = localBlock.params[0]) === null || _e === void 0 ? void 0 : _e.filter;
        }
        else {
            const imgAttributes = extractImgAttributes(localBlock.template);
            if (imgAttributes) {
                const { src, dataName } = imgAttributes;
                value = src;
                name = dataName;
                errors = null;
                thumb = '';
                filter = '';
            }
        }
        updateSpecialBlockAttributesInContainer(id, name, value, thumb, errors, filter, imgUrl);
        setShowUrl(false);
    };
    const extractImgAttributes = (element) => {
        const regex = /<img.*?src="(.*?)".*?data-name="(.*?)"/;
        const match = element.match(regex);
        if (match && match.length > 2) {
            const src = match[1];
            const dataName = match[2];
            return { src, dataName };
        }
        return null;
    };
    // ----------------------
    // uploadFiles (pdf)
    // ----------------------
    const uploadFiles = (e) => {
        setUploading(true);
        setProgress(0);
        const formData = new FormData();
        if (e.target.files) {
            _forEach(e.target.files, (file, i) => {
                formData.append(`file${i}`, file);
            });
        }
        uploadPDF(formData)
            .then((response) => {
            setUploading(false);
            setImgUrl(response.data);
            const options = {
                customUI: ({ onClose }) => {
                    return (_jsxs("div", { className: "react-confirm-alert-body", children: [_jsx("h1", { children: appMessages.upload_pdf_title }), _jsx("p", { children: appMessages.upload_pdf_status_done }), _jsx("div", { className: "react-confirm-alert-button-group", style: { width: '100%', margin: '0 auto' }, children: _jsx(Button, { style: { margin: '0 auto', marginTop: '20px', width: '100px' }, variant: "info", onClick: onClose, children: "OK" }) })] }));
                },
                childrenElement: () => _jsx("div", {}),
                closeOnEscape: true,
                closeOnClickOutside: true,
            };
            confirmAlert(options);
        })
            .catch(() => {
            setUploading(false);
            const options = {
                customUI: ({ onClose }) => {
                    return (_jsxs("div", { className: "react-confirm-alert-body", children: [_jsx("h1", { children: appMessages.upload_pdf_title }), _jsx("p", { children: appMessages.upload_pdf_status_error }), _jsx("div", { className: "react-confirm-alert-button-group", style: { width: '100%', margin: '0 auto' }, children: _jsx(Button, { style: { margin: '0 auto', marginTop: '20px', width: '100px' }, variant: "info", onClick: onClose, children: "OK" }) })] }));
                },
                childrenElement: () => _jsx("div", {}),
                closeOnEscape: true,
                closeOnClickOutside: true,
            };
            confirmAlert(options);
        });
    };
    // ----------------------
    // Render (JSX)
    // ----------------------
    const { type, pageZoom, spaceConsumed, spaceTotal, settings, index, id, usedMedia, containerSelect, rasterV, pasteData, copiedBlock, attributes, showKeyboard, data, isSubmission, wlAdminUser, colorSettings, setSelectedObjectForAI, selectedObjectForAI, } = props;
    /* Warning - Hint
      The current template CSS settings are breaking the options menu scaling
      within the draggable container. please check your CSS settings, such as...
  
     .react-draggable i::before {
        font-size: 8rem !important;
       }
  
      .react-draggable i {
        padding: 0.5em 0.59em !important;
      }
    */
    const zoomEqualizer = 1 / parseFloat(pageZoom);
    const styleOptions = { transform: `scale(${zoomEqualizer})` };
    /* No bug in the system, just an incorrect css definition in template settings.
      The draggable i definitions should be supplemented with the specific arrow class (fa-arrows)
      as shown in this example
      .react-draggable i.fa-arrows { … };
      .react-draggable i.fa-arrows:hover { … };
      .react-draggable i.fa-arrows::before { … };
   */
    let pasteButton = '';
    let _sortedBlocks;
    let _blocks;
    if (copiedBlock !== null && copiedBlock !== undefined) {
        _sortedBlocks = getSortedBlocksCopy(data.container_space, data.container_cols, data.blocks);
        _blocks = getMaxFreeSpaceInContainer(_sortedBlocks, copiedBlock[0].page_space_needed);
    }
    // helper functions needed in JSX (copied from previous implementation)
    function getSortedBlocksCopy(cRow, cCol, blocksArg) {
        let col = 1;
        const cRowInit = cRow;
        const sortedBlock = [];
        for (let i = 0; i < blocksArg.length; i++) {
            if (blocksArg[i].page_space_needed <= cRow) {
                for (let a = 0; a < sortedBlock.length; a++) {
                    if (sortedBlock.length > 0 && sortedBlock[a].col === col) {
                        sortedBlock[a].R = cRow - blocksArg[i].page_space_needed;
                    }
                }
                cRow -= blocksArg[i].page_space_needed;
            }
            else {
                col += 1;
                cRow = cRowInit;
                cRow -= blocksArg[i].page_space_needed;
            }
            sortedBlock.push({ id: blocksArg[i].id, col, R: cRow, disabled: true });
        }
        return sortedBlock;
    }
    function getMaxFreeSpaceInContainer(_sortedBlocksArg, spaceNeeded) {
        let maxFilledCol = 0;
        let sortedBlocksNew = [];
        _forEach(_sortedBlocksArg, (sBlock) => {
            maxFilledCol = sBlock.col;
        });
        if (maxFilledCol + 1 < props.data.container_cols && props.data.container_cols >= spaceNeeded) {
            _forEach(_sortedBlocksArg, (sBlock) => {
                sBlock.disabled = false;
            });
            sortedBlocksNew = _sortedBlocksArg;
        }
        if (maxFilledCol < props.data.container_cols && props.data.container_cols >= spaceNeeded) {
            _forEach(_sortedBlocksArg, (sBlock) => {
                sBlock.disabled = true;
            });
            for (let i = 1; i <= maxFilledCol; i++) {
                const filteredBlocks = _.filter(_sortedBlocksArg, (x) => x.col === i);
                if (filteredBlocks.length > 0) {
                    filteredBlocks[filteredBlocks.length - 1].disabled = false;
                }
                sortedBlocksNew = sortedBlocksNew.concat(filteredBlocks);
            }
        }
        if (maxFilledCol === props.data.container_cols) {
            _forEach(_sortedBlocksArg, (sBlock) => {
                if (sBlock.R < spaceNeeded) {
                    sBlock.disabled = true;
                }
                else {
                    sBlock.disabled = false;
                }
                sortedBlocksNew = _sortedBlocksArg;
            });
        }
        return sortedBlocksNew;
    }
    return (_jsxs("div", { className: `blockContainer ${attributes.className}`, onClick: containerSelect, children: [_jsx(DndContext, { sensors: sensors, onDragEnd: handleDragEnd, children: !blocks
                    ? null
                    : blocks.map((block, i) => {
                        var _a, _b, _c, _d, _e, _f;
                        const sectionClassPre = block.template ? block.template.match(/(?:data-section=\").*?(?=\s*\")/gm) : [''];
                        const sectionClass = sectionClassPre ? ' ' + sectionClassPre[0].replace('data-section="', '') : '';
                        const blockUpDisable = i === 0;
                        const blockDownDisable = i === blocks.length - 1;
                        const blockDeleteDisable = false;
                        const blockEditDisable = false;
                        const blockCopyDisable = false;
                        if (pasteData && type === 'active') {
                            pasteButton = (_jsx(Button, { "data-tooltip-content": appMessages.editor.paste_content_after, disabled: props.blockPasteDisable === false
                                    ? _blocks
                                        ? (_a = _blocks[i]) === null || _a === void 0 ? void 0 : _a.disabled
                                        : true
                                    : props.blockPasteDisable, "data-tooltip-id": "blockinfo", "data-block": block.id, "data-parent": props.id, onClick: pasteBlock, children: _jsx("i", { className: "fas fa-sign-in fa-rotate-90", "data-block": block.id, "data-parent": props.id }) }));
                        }
                        const blockHeight = rasterV * block.page_space_needed;
                        const innerBlockHeight = { height: `${blockHeight}px` };
                        const sectionRef = createRef();
                        let error = '';
                        let errorText = '';
                        if (block.errors.overflow) {
                            error = 'overflow';
                            errorText = appMessages.editor.text_overflow;
                        }
                        if (block.errors.default_image) {
                            error += ' default-image';
                            errorText += appMessages.editor.initial_image_available;
                        }
                        let inlineClass = '';
                        if (block.inline_block) {
                            inlineClass = ' inline-block col-section';
                        }
                        const heightClass = ' height_' + block.page_space_needed;
                        let sectionColor = '';
                        if (props.getColorSettings) {
                            sectionColor = props.getColorSettings(block === null || block === void 0 ? void 0 : block.id, id);
                        }
                        let sectionFontSize = ' ';
                        sectionFontSize = ` ${(_b = props.fontSizeSettings[block === null || block === void 0 ? void 0 : block.id]) !== null && _b !== void 0 ? _b : ' '} `;
                        // determine draggable default position from deltaPosition
                        // const dp = deltaPosition.find((d) => d.blockId === block.id);
                        // const defaultPos = { x: dp?.x ?? block.position_x ?? 0, y: dp?.y ?? block.position_y ?? 0 };
                        let positionX;
                        let positionY;
                        positionX = 0;
                        positionY = 0;
                        if (block.position_x !== undefined && block.position_x !== null) {
                            positionX = block.position_x || 0;
                        }
                        if (block.position_y !== undefined && block.position_y !== null) {
                            positionY = block.position_y || 0;
                        }
                        const blockPosition = { x: positionX, y: positionY };
                        let selectedObjectClass = '';
                        if (block.id === ((_c = selectedObjectForAI === null || selectedObjectForAI === void 0 ? void 0 : selectedObjectForAI.block) === null || _c === void 0 ? void 0 : _c.id)) {
                            selectedObjectClass = 'selectedObject ';
                        }
                        return (_jsxs(DraggableWrapper, { id: block.id, position: blockPosition, pageZoom: parseFloat(pageZoom), onDragPosition: (id, x, y) => {
                                dragOffsets.current[id] = { x, y };
                            }, children: [_jsx("i", { className: "fas fa-arrows", style: { cursor: 'move', paddingRight: 6 } }), _jsxs("section", { ref: sectionRef, style: innerBlockHeight, "data-tooltip-content": errorText, "data-tooltip-id": "errorinfo", className: heightClass + inlineClass + sectionClass + sectionColor + sectionFontSize + selectedObjectClass, children: [type === 'active' ? (_jsx(EditorBlockFTs, { type: type, appIntl: appIntl, data: block, settings: settings, updateParam: updateParam, updateInnerBlock: props.updateInnerBlock, zoomEqualizer: zoomEqualizer, index: `${index}.${id}.${i}`, pageZoom: pageZoom, imageUpload: _imageUpload, usedMedia: usedMedia, rasterV: rasterV, publisherInfo: props.publisherInfo, bgColor: bgColor[block.id], rootUser: props.rootUser, wlAdminUser: wlAdminUser, isSubmission: isSubmission, uploadError: props.uploadError, getDeviceType: props.getDeviceType, showKeyboard: showKeyboard, resizeBlock: props.resizeBlock, blockBiggerDisable: props.blockBiggerDisable, autoResizeSupported: false, setSelectedColor: props.setSelectedColor, currentPage: props.currentPage, getColorSettings: props.getColorSettings, deleteSelectedColor: props.deleteSelectedColor, colorSettings: colorSettings, setFontSizeSettings: props.setFontSizeSettings, fontSizeSettings: props.fontSizeSettings, blockContainerId: id, navEntry: props.navEntry, resetNavEntry: props.resetNavEntry, saveDescription: props.saveDescription, onlyWhitelabelAdminCanModifyBlocks: props.onlyWhitelabelAdminCanModifyBlocks, updateBlocksState: props.updateBlocksState, setSelectedObjectForAI: setSelectedObjectForAI, selectedObjectForAI: selectedObjectForAI }, `${id}.${i}`)) : (_jsx(PreviewBlockFTs, { appIntl: appIntl, data: block, index: `${index}.${id}.${i}`, setFontSizeSettings: props.setFontSizeSettings, fontSizeSettings: props.fontSizeSettings, updateParam: updateParam, currentPage: props.currentPage, setSelectedColor: props.setSelectedColor, blockContainerId: id, usedMedia: usedMedia, rasterV: rasterV, getColorSettings: props.getColorSettings, updateInnerBlock: props.updateInnerBlock, imageUpload: _imageUpload, colorSettings: colorSettings, updateBlocksState: props.updateBlocksState, settings: settings }, `${id}.${i}`)), type === 'active' ? (_jsxs("div", { className: "block-options", style: styleOptions, children: [_jsxs(ButtonGroup, { vertical: true, size: "sm", children: [_jsx(Button, { "data-tooltip-content": appMessages.editor.up, "data-tooltip-id": "blockinnerinfo", disabled: blockUpDisable, "data-block": block.id, "data-index": i, "data-target": i - 1, onClick: moveBlock, variant: "light", children: _jsx("i", { className: "far fa-angle-up", "data-block": block.id, "data-index": i, "data-target": i - 1 }) }), _jsxs("div", { className: "toggle-edit btn btn-light", "data-block": block.id, children: [_jsx("i", { className: "fas fa-edit", "data-block": block.id }), _jsxs(ButtonGroup, { className: "edit-block", size: "sm", vertical: true, children: [_jsx(Button, { "data-tooltip-content": appMessages.editor.reset_block, "data-tooltip-id": "blockinnerinfo", "data-block": block.id, "data-template": block.template_id, onClick: resBlock, variant: "light", children: _jsx("i", { className: "fas fa-retweet", "data-block": block.id, "data-template": block.template_id }) }), block.template && block.template.includes('<img') && (_jsx(Button, { "data-tooltip-content": appMessages.url_settings, "data-tooltip-id": "blockinfo", "data-block": block.id, "data-template": block.template_id, onClick: () => getImageUrlSettings(block), variant: "light", children: _jsx("i", { className: "fad fa-link", "data-block": block.id, "data-template": block.template_id }) })), _jsx(Button, { "data-tooltip-content": appMessages.editor.copy_block, "data-tooltip-id": "blockinnerinfo", disabled: blockCopyDisable, "data-block": block.id, "data-parent": props.id, onClick: (e) => copyBlock(e, block.id), variant: "light", children: _jsx("i", { className: "fas fa-clone", "data-block": block.id, "data-parent": props.id }) }), block.template && block.template.includes('data-background=') && (_jsxs(Button, { "data-block": block.id, "data-parent": props.id, className: "toggle_bg", variant: "light", children: [_jsx("i", { className: "fas fa-fill-drip", "data-block": block.id, "data-parent": props.id }), _jsxs(ButtonGroup, { className: "edit-color", size: "sm", vertical: true, children: [(_e = (_d = props.settings.target) === null || _d === void 0 ? void 0 : _d.back_colors) === null || _e === void 0 ? void 0 : _e.map((bgColor) => {
                                                                                            var _a, _b;
                                                                                            const getBackgroundAttr = block.template.match(/(?:data-background=\").*?(?=\s*\")/gm);
                                                                                            if (((_a = getBackgroundAttr === null || getBackgroundAttr === void 0 ? void 0 : getBackgroundAttr[0]) === null || _a === void 0 ? void 0 : _a.includes(bgColor.label[0].name)) ||
                                                                                                ((_b = getBackgroundAttr === null || getBackgroundAttr === void 0 ? void 0 : getBackgroundAttr[0]) === null || _b === void 0 ? void 0 : _b.includes('true'))) {
                                                                                                return (_jsx(Button, { "data-tooltip-content": appMessages.editor.change_backgroundcolor, "data-tooltip-id": "blockinfo", "data-block": bgColor.id, "data-parent": block.id, onClick: () => updateBackground(bgColor.label[0].name, block.id), style: { padding: 4 }, variant: "light", children: _jsx("p", { style: {
                                                                                                            width: 20,
                                                                                                            height: 20,
                                                                                                            padding: 0,
                                                                                                            margin: 0,
                                                                                                            background: bgColor.color,
                                                                                                        } }) }, bgColor.id));
                                                                                            }
                                                                                            return null;
                                                                                        }), (_f = props.settings.back_colors) === null || _f === void 0 ? void 0 : _f.map((bgColor) => {
                                                                                            var _a, _b;
                                                                                            const getBackgroundAttr = block.template.match(/(?:data-background=\").*?(?=\s*\")/gm);
                                                                                            if (((_a = getBackgroundAttr === null || getBackgroundAttr === void 0 ? void 0 : getBackgroundAttr[0]) === null || _a === void 0 ? void 0 : _a.includes(bgColor.label[0].name)) ||
                                                                                                ((_b = getBackgroundAttr === null || getBackgroundAttr === void 0 ? void 0 : getBackgroundAttr[0]) === null || _b === void 0 ? void 0 : _b.includes('true'))) {
                                                                                                return (_jsx(Button, { "data-tooltip-content": appMessages.editor.change_backgroundcolor, "data-tooltip-id": "blockinfo", "data-block": bgColor.id, "data-parent": props.id, onClick: () => updateBackground(bgColor.label[0].name, block.id), style: { padding: 4 }, variant: "light", children: _jsx("p", { style: {
                                                                                                            width: 20,
                                                                                                            height: 20,
                                                                                                            padding: 0,
                                                                                                            margin: 0,
                                                                                                            background: bgColor.color,
                                                                                                        } }) }, bgColor.id));
                                                                                            }
                                                                                            return null;
                                                                                        })] })] })), _jsx(Button, { "data-tooltip-content": appMessages.editor.delete_block, "data-tooltip-id": "blockinnerinfo", disabled: blockDeleteDisable, "data-block": block.id, "data-space": block.page_space_needed, onClick: deleteBlock, variant: "light", children: _jsx("i", { className: "fas fa-trash", "data-block": block.id, "data-space": block.page_space_needed }) })] })] }), pasteButton, _jsx(Button, { "data-tooltip-content": appMessages.editor.down, "data-tooltip-id": "blockinnerinfo", disabled: blockDownDisable, "data-block": block.id, "data-index": i, "data-target": i + 1, onClick: moveBlock, variant: "light", children: _jsx("i", { className: "far fa-angle-down", "data-block": block.id, "data-index": i, "data-target": i + 1 }) })] }), _jsx(Tooltip, { id: "blockinnerinfo", place: "left" })] })) : null] }, block === null || block === void 0 ? void 0 : block.id)] }, block.id));
                    }) }), showUrl && (_jsx(ContextModal, { children: _jsx("div", { id: "contextModalWrap", children: _jsxs("div", { id: "contextModalStage", className: "row", children: [_jsx("h1", { children: appMessages.url_settings }), _jsx(FormInput, { type: "text", label: "Url", name: "url", onChange: (name, value) => {
                                    setImgUrl(value);
                                }, value: imgUrl }), _jsxs("div", { className: "uploadPDF", children: [_jsx("label", { htmlFor: "file-input", style: { cursor: 'pointer' }, children: _jsx("p", { children: _jsxs("span", { style: { fontSize: '15px' }, children: ["+ ", _jsx("i", { className: "far fa-file-pdf" }), " ", appMessages.upload_pdf] }) }) }), _jsx("input", { style: { display: 'none' }, id: "file-input", type: "file", accept: "application/pdf", onChange: (e) => {
                                            uploadFiles(e);
                                        } }), uploading ? _jsx(Loader, { small: true }) : _jsx(_Fragment, {})] }), _jsxs("div", { className: "modal-default-style", children: [_jsx(Button, { variant: "success", size: "lg", style: { position: 'fixed', left: '8vw', top: '85%', zIndex: '100' }, onClick: () => {
                                            saveUrl();
                                        }, children: appMessages.editor.use_filter }), _jsx(Button, { variant: "danger", size: "lg", onClick: handleIEClose, className: "cancelIE", children: appMessages.editor.abort })] })] }) }) }))] }));
};
// ============================
// Redux connection
// ============================
const mapStateToProps = (state) => {
    return {
        user: state.login.user,
        appIntl: state.intl,
        editionRV: state.editor.editionRV,
        pendingR: state.editor.pendingR,
        savingR: state.editor.savingR,
        unsavedR: state.editor.unsavedR,
        linkDataR: state.editor.linkDataR,
        campaignOptionsR: state.editor.campaignOptionsR,
        forwardingR: state.editor.forwardingR,
        previewingR: state.editor.previewingR,
        exportingR: state.editor.exportingR,
        printingR: state.editor.printingR,
        printx: state.editor.printx,
        changesR: state.editor.changesR,
    };
};
const mapDispatchToProps = (dispatch) => {
    return {
        setLoginForwardUrl: (forwardUrl) => {
            dispatch(setLoginForwardUrl(forwardUrl));
        },
        getEditionR: (data) => {
            dispatch(getEditionR(data));
        },
        save_ContentR: (data, datab) => {
            dispatch(save_ContentR(data, datab));
        },
        setVariables: (data) => {
            dispatch(setVariables(data));
        },
        saveToR: (data) => {
            dispatch(saveToR(data));
        },
        initTemplateR: (data, datab) => {
            dispatch(initTemplateR(data, datab));
        },
        getCampaignsR: () => {
            dispatch(getCampaignsR());
        },
        setChangesR: (data) => {
            dispatch(setChangesR(data));
        },
    };
};
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(legacyInjectIntl(DraggableContainerFTs)));
